/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { Bars2Icon, XMarkIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import type { Identifier, XYCoord } from 'dnd-core'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { ItemTypes } from './ItemTypes'

export interface CardProps {
  id: any
  index: number
  moveCard: (dragIndex: number, hoverIndex: number) => void
  /** Omit to make the item reorderable but not removable. */
  onRemove?: (index: number) => void
  children: React.ReactNode
  className?: string
}

interface DragItem {
  index: number
  id: string
  type: string
}

export default function DraggableItem({ id, index, moveCard, onRemove, children, className }: CardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveCard(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id, index }
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))
  return (
    <div
      ref={ref}
      className={classNames(
        `flex cursor-move items-center justify-between rounded-md border border-default bg-surface px-2 py-2 shadow-elevation-1`,
        isDragging ? `opacity-0` : `opacity-100`,
        className,
      )}
      data-handler-id={handlerId}
    >
      <div className="flex w-full items-center">
        <Bars2Icon className="h-5 w-5 fill-current text-default" />
        <div id={id} className="ml-2 grow text-body-sm text-default">
          {children}
        </div>
      </div>
      {onRemove && (
        <XMarkIcon onClick={() => onRemove(index)} className="h-5 w-5 cursor-pointer fill-current text-default" />
      )}
    </div>
  )
}
